import { memo, type MouseEvent, type ReactNode } from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import RefreshIcon from "@mui/icons-material/Refresh";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import {
  communicationsSubsectionAccordionDetailsSx,
  communicationsSubsectionAccordionSummarySx,
  communicationsSubsectionAccordionSx,
  communicationsSubsectionIconBoxSx,
} from "./communicationsSubsectionStyles";

export interface CommunicationsSubsectionPanelProps {
  icon: ReactNode;
  title: string;
  subtitle?: string;
  children: ReactNode;
  /** Estado inicial do accordion (padrão: expandido). */
  defaultExpanded?: boolean;
  /** Identificador para acessibilidade (aria-controls / id). */
  panelId?: string;
  onRefresh?: () => void;
  isRefreshing?: boolean;
}

/**
 * Subseção colapsável da Gestão de comunicações (FluxZap), alinhada ao SectionHeader.
 */
const CommunicationsSubsectionPanel = memo(
  ({
    icon,
    title,
    subtitle,
    children,
    defaultExpanded = true,
    panelId = "communications-subsection",
    onRefresh,
    isRefreshing = false,
  }: CommunicationsSubsectionPanelProps) => {
    const headerId = `${panelId}-header`;
    const contentId = `${panelId}-content`;

    // Evita que o clique no refresh expanda/colapse o AccordionSummary.
    const stopAccordionToggle = (event: MouseEvent) => {
      event.stopPropagation();
    };

    return (
      <Accordion
        defaultExpanded={defaultExpanded}
        disableGutters
        elevation={0}
        sx={communicationsSubsectionAccordionSx}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls={contentId}
          id={headerId}
          sx={{
            ...communicationsSubsectionAccordionSummarySx,
            // Espaçamento real entre o refresh (no content) e o expand (slot do MUI).
            ...(onRefresh
              ? {
                  "& .MuiAccordionSummary-expandIconWrapper": {
                    color: "text.secondary",
                    ml: 1.5,
                    mr: 1,
                  },
                }
              : null),
          }}
        >
          <Stack
            direction="row"
            spacing={1.25}
            alignItems="flex-start"
            sx={{ width: "100%" }}
          >
            <Paper variant="outlined" sx={communicationsSubsectionIconBoxSx}>
              {icon}
            </Paper>
            <Stack spacing={0.25} sx={{ minWidth: 0, flex: 1, pr: 0.5 }}>
              <Typography variant="subtitle2" fontWeight={700}>
                {title}
              </Typography>
              {subtitle ? (
                <Typography variant="caption" color="text.secondary">
                  {subtitle}
                </Typography>
              ) : null}
            </Stack>

            {/* Fora do expandIconWrapper — não gira nem inverte a ordem ao expandir. */}
            {onRefresh ? (
              <Tooltip title="Atualizar extrato de envios">
                <span style={{ alignSelf: "center", display: "inline-flex" }}>
                  <IconButton
                    size="small"
                    aria-label="Atualizar extrato de envios"
                    disabled={isRefreshing}
                    onClick={(event) => {
                      stopAccordionToggle(event);
                      onRefresh();
                    }}
                    onMouseDown={stopAccordionToggle}
                    sx={{ color: "text.secondary" }}
                  >
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            ) : null}
          </Stack>
        </AccordionSummary>

        <AccordionDetails
          id={contentId}
          sx={communicationsSubsectionAccordionDetailsSx}
        >
          {children}
        </AccordionDetails>
      </Accordion>
    );
  },
);

CommunicationsSubsectionPanel.displayName = "CommunicationsSubsectionPanel";

export default CommunicationsSubsectionPanel;
